import { useEffect, useMemo, useRef, useState } from "react";
import type { NavigationSection } from "./TabbedDialog.types";

/** Duration (in ms) to wait after programmatic scrolling before re-enabling scroll observation */
const SCROLL_TIMEOUT = 1000;

/** Offset (in px) from the top of the container when scrolling to a section */
const SCROLL_OFFSET = 20;

/**
 * IntersectionObserver root margin configuration.
 * Creates a viewport that starts 20% from the top and ends 70% from the bottom,
 * effectively observing the middle 10% of the scrollable area.
 */
const OBSERVER_ROOT_MARGIN = "-20% 0px -70% 0px";

/**
 * Threshold values for intersection detection.
 * Triggers callback at 0%, 50%, and 100% visibility.
 */
const OBSERVER_THRESHOLD = [0, 0.5, 1];

/**
 * Minimum intersection ratio required for a section to be considered "visible".
 * A value of 0.6 means at least 60% of the element must be in the observer viewport.
 */
const INTERSECTION_RATIO_MIN = 0.6;

/**
 * Custom hook that synchronizes navigation highlighting with content scrolling.
 *
 * This hook uses IntersectionObserver to track which section is currently visible
 * in the scroll container and provides a function to programmatically scroll to
 * specific sections. It intelligently disables automatic tracking during programmatic
 * scrolling to prevent conflicts.
 *
 * @param contentRef - Reference to the scrollable content container element
 * @param sections - Array of navigation sections, each potentially containing subsections and grandchildren
 * @param isOpen - Whether the dialog/component is currently open (observer only runs when true)
 *
 * @returns Object containing:
 *   - `activeSection`: ID of the currently visible section (or null if none)
 *   - `scrollToSection`: Function to programmatically scroll to a section by ID
 *
 * @example
 * ```tsx
 * const contentRef = useRef<HTMLDivElement>(null);
 * const sections = [
 *   { id: 'intro', label: 'Introduction' },
 *   { id: 'details', label: 'Details', subsections: [
 *     { id: 'sub1', label: 'Subsection 1', grandchildren: [
 *       { id: 'grand1', label: 'Grandchild 1' }
 *     ]}
 *   ]}
 * ];
 *
 * const { activeSection, scrollToSection } = useScrollSync(
 *   contentRef,
 *   sections,
 *   isDialogOpen
 * );
 *
 * // Later in render:
 * <button onClick={() => scrollToSection('intro')}>Go to Intro</button>
 * ```
 */
export function useScrollSync(
  contentRef: React.RefObject<HTMLDivElement | null>,
  sections: NavigationSection[],
  isOpen: boolean,
  activeTabId?: string,
) {
  /** Currently active/visible section ID */
  const [activeSection, setActiveSection] = useState<string | null>(null);

  /** IntersectionObserver instance for tracking section visibility */
  const observerRef = useRef<IntersectionObserver | null>(null);

  /**
   * Flag to prevent observer updates during programmatic scrolling.
   * When true, the IntersectionObserver callback exits early to avoid
   * competing with the manual setActiveSection call.
   */
  const isScrollingRef = useRef(false);

  /** Timeout handle for re-enabling observation after programmatic scroll */
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Flattens the nested section structure into a list of all section IDs.
   * Includes section IDs, subsection IDs, and grandchild IDs.
   */
  const allSectionIds = useMemo(
    () =>
      sections.flatMap((section) => [
        section.id,
        ...(section.subsections?.flatMap((sub) => [
          sub.id,
          ...(sub.grandchildren?.map((grand) => grand.id) || []),
        ]) || []),
      ]),
    [sections],
  );

  /**
   * Sets up IntersectionObserver to track visible sections.
   *
   * The observer:
   * 1. Only runs when the dialog is open and not programmatically scrolling
   * 2. Identifies the topmost visible section that meets the intersection ratio threshold
   * 3. Updates the active section state to highlight the correct navigation item
   *
   * Cleanup: Disconnects observer and clears timeouts when dependencies change or unmount
   */
  useEffect(() => {
    if (!contentRef.current || !isOpen) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        // Skip updates during programmatic scrolling to prevent conflicts
        if (isScrollingRef.current) return;

        // Filter for visible entries meeting the minimum intersection threshold,
        // then sort by vertical position to find the topmost visible section
        const visibleEntries = entries
          .filter(
            (entry) =>
              entry.isIntersecting &&
              entry.intersectionRatio >= INTERSECTION_RATIO_MIN,
          )
          .sort((a, b) => {
            const aRect = a.boundingClientRect;
            const bRect = b.boundingClientRect;
            return aRect.top - bRect.top;
          });

        // Set the topmost visible section as active
        if (visibleEntries.length > 0 && visibleEntries[0]) {
          setActiveSection(visibleEntries[0].target.id);
        }
      },
      {
        root: contentRef.current,
        rootMargin: OBSERVER_ROOT_MARGIN,
        threshold: OBSERVER_THRESHOLD,
      },
    );

    // Observe all section elements
    for (const sectionId of allSectionIds) {
      const element = document.getElementById(sectionId);
      if (element && observerRef.current) {
        observerRef.current.observe(element);
      }
    }

    return () => {
      observerRef.current?.disconnect();
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [sections, isOpen, allSectionIds, contentRef, activeTabId]);

  /**
   * Programmatically scrolls to a specific section and updates navigation highlight.
   *
   * During the scroll:
   * 1. Sets the target section as active immediately (optimistic update)
   * 2. Disables IntersectionObserver updates to prevent conflicts
   * 3. Performs smooth scroll with offset for better positioning
   * 4. Re-enables observer after SCROLL_TIMEOUT duration
   *
   * @param sectionId - The ID of the section to scroll to
   */
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (!element || !contentRef.current) return;

    const container = contentRef.current;

    // Clear any existing timeout to reset the scroll lock duration
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Optimistically set active section and disable observer
    setActiveSection(sectionId);
    isScrollingRef.current = true;

    // Use getBoundingClientRect for accurate positioning
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const scrollPosition =
      container.scrollTop +
      (elementRect.top - containerRect.top) -
      SCROLL_OFFSET;

    container.scrollTo({
      top: scrollPosition,
      behavior: "smooth",
    });

    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
      scrollTimeoutRef.current = null;
    }, SCROLL_TIMEOUT);
  };

  return { activeSection, scrollToSection };
}
